import { useCallback, useEffect, useState } from 'react'
import { Settings2 } from 'lucide-react'
import {
  Bidi, Button, Card, CardHeader, ErrorState, Field, FieldInput, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  fetchConfiguration, formatCount, may, saveHotel, saveRoleName, translateError,
  type ConfigurationRole, type ConfigurationView,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { ShiftsCard } from './ShiftsCard'
import { WorkTriggersCard } from './WorkTriggersCard'
import { RequestCategories } from './RequestCategories'
import { FaultCategories } from './FaultCategories'

/* ============================================================================
   W-129 · THE HOTEL'S OWN WORDS.  Sprint 55, for CQ-130.

   ── WHY THIS SCREEN EXISTS, AND WHY NOW ──────────────────────────────────────────────
   Every Arabic name in this product is OURS AND INVENTED, and every place that draws one
   says so in writing: the hotel's own name, the six role names, the task types, the stock
   items. The client answered CQ-130 on 18 September — **the invented words stay, and the
   hotel corrects them from the settings during go-live.**

   **THAT ANSWER WAS NOT TRUE UNTIL THIS FILE.** `Property.NameAr` and `Role.NameAr` have
   been in the model since Sprints 30 and 25 and nothing could write either. A column no
   screen can reach is a promise nobody has read back.

   ── WHAT IS DELIBERATELY NOT HERE ────────────────────────────────────────────────────
   Pack 5 draws shifts, inspection sampling, quiet hours, linen sets in play, the deep-clean
   cycle, occupancy sync and "copy to Karbala" on this screen. Four of those govern features
   this product does not have and one of them is TQ-01. **A row that sets nothing is worse
   than a row that is absent**: an administrator who changes "inspection sampling" and sees
   nothing happen learns that this screen does not work, and then does not believe the row
   that does.

   ── TWO SAVES, NOT ONE ───────────────────────────────────────────────────────────────
   The hotel's details and a role's name are two different acts with two different rows in
   the activity log. One "save configuration" button covering both would record that
   somebody changed something.
   ========================================================================= */

/** Named placeholders, because Arabic and English do not order their clauses the same. */
function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole)
}

function pluralOf(forms: Record<string, string>, count: number, locale: 'en' | 'ar'): string {
  return forms[new Intl.PluralRules(locale).select(count)] ?? forms.other ?? ''
}

/**
 * **THE FORM'S FIELDS, IN ONE PLACE.** What the screen loads into the boxes and what
 * "has anything changed" compares against are the same object by construction — so a
 * field added to one cannot be missing from the other. See `hotelChanged`.
 */
const draftOf = (view: ConfigurationView) => ({
  name: view.name,
  nameAr: view.nameAr ?? '',
  timeZone: view.timeZone,
  defaultLanguage: view.defaultLanguage,
  requireInspection: view.requireInspection,
})

export function Configuration({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [view, setView] = useState<ConfigurationView | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [saving, setSaving] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  /* The hotel's four fields while they are being edited. Held as one draft rather than
     four states so that "has anything changed" is one comparison. */
  const [hotel, setHotel] = useState({
    name: '', nameAr: '', timeZone: '', defaultLanguage: '', requireInspection: true,
  })
  /** One role's two fields, by role id, while it is being edited. */
  const [roles, setRoles] = useState<Record<string, { name: string; nameAr: string }>>({})

  /* **TWO PERMISSIONS, TWO CARDS, ONE SCREEN.** Sprint 56.

     `admin.configuration` is the administrator's: the hotel's name, its time zone, what the
     roles are called. `admin.requestCategories` is the HOUSEKEEPING MANAGER's: what a guest
     may ask for and how long each kind is promised in — the decision made by the person who
     knows there is no turndown during Ramadan.

     They are separate because they are held by different people, and that means this screen
     opens for either of them and draws only the card they hold. Gating the whole screen on
     the administrator's permission would have made the split pointless: the manager would
     hold a right with no way to exercise it, which is precisely the shape of defect the
     permission catalogue exists to make visible.

     **AND THE GRANT GATES THE REQUEST, NOT ONLY THE DRAWING** — W-107's lesson. */
  const allowed = may('admin.configuration')
  const allowedCategories = may('admin.requestCategories')
  /* **W-75'S OWN GRANT** (Sprint 63). The two lists belong to two people — what a guest
     may ask for is the housekeeping manager's, what can be broken in a room is
     maintenance's — so a hotel that has moved one and not the other gets exactly what it
     set, which is the point of them being separate cells on W-122. */
  const allowedFaults = may('admin.faultCategories')

  const take = useCallback((next: ConfigurationView) => {
    setView(next)
    setHotel(draftOf(next))
    setRoles(Object.fromEntries(next.roles.map((r) =>
      [r.roleId, { name: r.name, nameAr: r.nameAr ?? '' }])))
  }, [])

  const load = useCallback(async () => {
    if (!allowed) return
    setError(null)
    try {
      take(await fetchConfiguration(propertyId))
    } catch (caught: unknown) {
      setError(caught)
    }
  }, [propertyId, allowed, take])

  useEffect(() => { void load() }, [load])

  if (!allowed && !allowedCategories && !allowedFaults) return <NoPermissionState />
  if (allowed && error !== null && view === null) {
    return <ErrorState body={translateError(error, locale).message} onRetry={() => void load()} />
  }
  if (allowed && view === null) return <LoadingState rows={5} />

  async function commit(what: string, run: () => Promise<ConfigurationView>) {
    setSaving(what); setError(null); setSaved(null)
    try {
      take(await run())
      setSaved(what)
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setSaving(null)
    }
  }

  /* `view` is null only for somebody who holds the categories permission and not this
     one — the hotel card is not drawn for them, so there is nothing to compare. */
  /* **DERIVED, NOT LISTED.** This was four hand-written comparisons, and adding the
     inspection policy in Sprint 77 did not add a fifth — so changing only that field left
     the save button disabled, which is a setting that cannot be saved. Caught by its own
     test on the first run.

     It is the shape this project has recorded more than any other: a list that has to be
     extended for the code to keep working is a defect with a delay on it. Comparing the
     whole draft against the same draft built from the view has no list to forget. */
  const hotelChanged = view !== null
    && JSON.stringify(hotel) !== JSON.stringify(draftOf(view))

  const roleChanged = (role: ConfigurationRole) => {
    const draft = roles[role.roleId]
    return draft !== undefined
      && (draft.name !== role.name || draft.nameAr !== (role.nameAr ?? ''))
  }

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader title={t.cfgTitle} icon={<Settings2 className="size-4" />} />
        <p className="max-w-[70ch] px-3.5 pb-3.5 text-[12.5px] leading-relaxed text-muted-foreground">
          {t.cfgIntro}
        </p>
      </Card>

      {error !== null ? (
        <div
          role="alert"
          className="rounded-hk border border-[#fca5a5] bg-[#fef2f2] px-3 py-2 text-[12px]
                     text-[#b91c1c] dark:border-[#7f1d1d] dark:bg-[#2a0f0f] dark:text-[#fca5a5]"
        >
          {String(translateError(error, locale).message)}
        </div>
      ) : null}

      {/* ── THE HOTEL ────────────────────────────────────────────────────── */}
      {allowed && view !== null ? (
      <Card>
        <CardHeader title={t.cfgHotel} />
        <div className="flex flex-col gap-3 p-3.5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t.cfgName}>
              {({ id, 'aria-label': label }) => (
                <FieldInput
                  id={id} aria-label={label} dir="auto" maxLength={120}
                  value={hotel.name}
                  onChange={(e) => setHotel((h) => ({ ...h, name: e.target.value }))}
                />
              )}
            </Field>

            <Field label={t.cfgNameAr} hint={t.cfgNameArHint}>
              {({ id, 'aria-label': label }) => (
                <FieldInput
                  id={id} aria-label={label} dir="rtl" lang="ar" maxLength={120}
                  value={hotel.nameAr}
                  onChange={(e) => setHotel((h) => ({ ...h, nameAr: e.target.value }))}
                />
              )}
            </Field>

            {/* SHOWN AND NOT OFFERED. See the server's `ConfigurationView`. */}
            <Field label={t.cfgCode} hint={t.cfgCodeHint}>
              {({ id, 'aria-label': label }) => (
                <FieldInput id={id} aria-label={label} value={view!.code} disabled readOnly />
              )}
            </Field>

            <Field label={t.cfgTimeZone} hint={t.cfgTimeZoneHint}>
              {({ id, 'aria-label': label }) => (
                /* **A LIST, NEVER A BOX.** A free-text zone is one typo away from a hotel
                   whose every date is wrong, and the failure shows up as a sweep that
                   throws at six in the morning rather than as anything on this screen. */
                <select
                  id={id}
                  aria-label={label}
                  dir="ltr"
                  className="w-full rounded-hk border border-input bg-background px-2 py-1.5
                             text-[12.5px] text-foreground outline-none
                             focus-visible:ring-2 focus-visible:ring-ring"
                  value={hotel.timeZone}
                  onChange={(e) => setHotel((h) => ({ ...h, timeZone: e.target.value }))}
                >
                  {view!.timeZones.map((zone) => (
                    <option key={zone} value={zone}>{zone}</option>
                  ))}
                </select>
              )}
            </Field>

            {/* ── THE INSPECTION POLICY (Sprint 77) ──────────────────────────────────
                **ENFORCED SINCE SPRINT 1 AND SETTABLE FROM NOWHERE.** The readiness rule
                reads it to decide whether a cleaned room may be sold, and task submission
                reads it to decide whether a room completes when the attendant hands it in
                (#129). A hotel that did not want to inspect had to be configured with a
                SQL statement — which is UAT prerequisite CF-04, ours to meet, and one we
                could not have met at a readiness review.

                It sits with the time zone rather than in a settings screen of its own
                because it is the same kind of fact: something about this hotel that
                changes how every other screen behaves. */}
            <Field label={t.cfgInspection} hint={t.cfgInspectionHint}>
              {({ id, 'aria-label': label }) => (
                <select
                  id={id}
                  aria-label={label}
                  className="w-full rounded-hk border border-input bg-background px-2 py-1.5
                             text-[12.5px] text-foreground outline-none
                             focus-visible:ring-2 focus-visible:ring-ring"
                  value={hotel.requireInspection ? 'yes' : 'no'}
                  onChange={(e) => setHotel(
                    (h) => ({ ...h, requireInspection: e.target.value === 'yes' }))}
                >
                  <option value="yes">{t.cfgInspectionYes}</option>
                  <option value="no">{t.cfgInspectionNo}</option>
                </select>
              )}
            </Field>

            <Field label={t.cfgLanguage} hint={t.cfgLanguageHint}>
              {({ id, 'aria-label': label }) => (
                <select
                  id={id}
                  aria-label={label}
                  className="w-full rounded-hk border border-input bg-background px-2 py-1.5
                             text-[12.5px] text-foreground outline-none
                             focus-visible:ring-2 focus-visible:ring-ring"
                  value={hotel.defaultLanguage}
                  onChange={(e) => setHotel((h) => ({ ...h, defaultLanguage: e.target.value }))}
                >
                  <option value="en">{t.cfgLanguageEn}</option>
                  <option value="ar">{t.cfgLanguageAr}</option>
                </select>
              )}
            </Field>
          </div>

          {/* **WHAT TURNING IT OFF ACTUALLY DOES**, said beside the control rather than
              discovered a fortnight later. A hotel that stops inspecting is not turning off
              a screen: every room goes on sale on one man's word, and every task completes
              itself the moment he says he is finished. */}
          {!hotel.requireInspection && (
            <p className="rounded-hk border border-[#fcd34d] bg-[#fffbeb] px-2.5 py-1.5
                          text-[11.5px] text-[#92400e] dark:border-[#b45309]
                          dark:bg-[#78350f]/25 dark:text-[#fcd34d]">
              {t.cfgInspectionOffWarning}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              disabled={!hotelChanged || saving === 'hotel'}
              aria-busy={saving === 'hotel'}
              onClick={() => void commit('hotel', () => saveHotel(propertyId, hotel))}
            >
              {saving === 'hotel' ? t.cfgSaving : t.cfgSaveHotel}
            </Button>
            {saved === 'hotel' && !hotelChanged ? (
              <span role="status" className="text-[12px] text-muted-foreground">
                {t.cfgHotelSaved}
              </span>
            ) : null}
          </div>
        </div>
      </Card>

      ) : null}

      {/* ── WHEN THIS HOTEL WORKS (Sprint 80) ────────────────────────────────
          Its own card and its own calls, for the reason the role names have theirs: a
          single "save configuration" covering the hotel's name and everybody's credit
          budget would record that somebody changed something. */}
      {allowed && view !== null ? <ShiftsCard propertyId={propertyId} /> : null}

      {/* **CQ-26 — WHAT RAISES WORK IN THIS HOTEL.** Sprint 83. It sits beside the shift
          hours because both are standing decisions about how the hotel runs, and both
          change what the board holds tomorrow morning. */}
      {allowed && view !== null ? <WorkTriggersCard propertyId={propertyId} /> : null}

      {/* ── THE ROLE NAMES ───────────────────────────────────────────────── */}
      {allowed && view !== null ? (
      <Card>
        <CardHeader title={t.cfgRoles} />
        <p className="max-w-[70ch] px-3.5 pt-0 text-[12.5px] leading-relaxed text-muted-foreground">
          {t.cfgRolesIntro}
        </p>

        <div className="overflow-x-auto p-3.5">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="p-2 text-start font-medium">{t.cfgColRole}</th>
                <th className="p-2 text-start font-medium">{t.cfgColName}</th>
                <th className="p-2 text-start font-medium">{t.cfgColNameAr}</th>
                <th className="p-2 text-start font-medium">{t.cfgColHeld}</th>
                <th className="p-2 text-start font-medium sr-only">{t.cfgSave}</th>
              </tr>
            </thead>
            <tbody>
              {view.roles.map((role) => {
                const draft = roles[role.roleId] ?? { name: role.name, nameAr: role.nameAr ?? '' }
                return (
                  <tr key={role.roleId} className="border-b border-border/60 last:border-0">
                    {/* **THE CODE IS THE ROW'S IDENTITY AND NEVER CHANGES.** It is also
                        the only thing on this row that still reads the same after
                        somebody has renamed everything. */}
                    <td className="p-2 font-mono text-[11.5px] text-muted-foreground">
                      {role.code}
                    </td>

                    <td className="p-2">
                      {/* **EACH BOX ASKS ITS OWN ROW'S QUESTION** — #152. Six boxes all
                          labelled "Name" is six boxes nobody using a screen reader can
                          tell apart. */}
                      <FieldInput
                        aria-label={`${t.cfgColName} — ${role.code}`}
                        dir="auto"
                        maxLength={80}
                        value={draft.name}
                        onChange={(e) => setRoles((r) => ({
                          ...r, [role.roleId]: { ...draft, name: e.target.value },
                        }))}
                      />
                    </td>

                    <td className="p-2">
                      <FieldInput
                        aria-label={`${t.cfgColNameAr} — ${role.code}`}
                        dir="rtl"
                        lang="ar"
                        maxLength={80}
                        value={draft.nameAr}
                        onChange={(e) => setRoles((r) => ({
                          ...r, [role.roleId]: { ...draft, nameAr: e.target.value },
                        }))}
                      />
                    </td>

                    <td className="p-2 text-muted-foreground">
                      {/* **HOW MANY PEOPLE HOLD IT HERE.** Renaming a role nine people hold
                          and renaming one nobody holds are different acts. */}
                      <Bidi>
                        {role.peopleHolding === 0
                          ? t.cfgNobodyHolds
                          : fill(pluralOf(t.cfgHeld, role.peopleHolding, locale),
                              { count: formatCount(role.peopleHolding, locale) })}
                      </Bidi>
                    </td>

                    <td className="p-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!roleChanged(role) || saving === role.roleId}
                        aria-busy={saving === role.roleId}
                        aria-label={`${t.cfgSave} — ${role.code}`}
                        onClick={() => void commit(role.roleId, () =>
                          saveRoleName(propertyId, role.roleId, draft))}
                      >
                        {saving === role.roleId ? t.cfgSaving : t.cfgSave}
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>
      ) : null}

      {/* ── WHAT A GUEST MAY ASK FOR (Sprint 56) ─────────────────────────── */}
      {allowedCategories ? (
        <RequestCategories propertyId={propertyId} locale={locale} onError={setError} />
      ) : null}

      {/* ── W-75, THE THIRD CARD (Sprint 63) ─────────────────────────────────
          Beside the requests list because they are the same job over two lists, and an
          administrator correcting one is usually correcting the other in the same
          sitting — but behind its OWN grant, because they are not the same right. */}
      {allowedFaults ? (
        <FaultCategories propertyId={propertyId} locale={locale} onError={setError} />
      ) : null}
    </div>
  )
}
